import React, { Suspense } from 'react';
import { Box, Grid, Typography, Paper } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { withRouter } from '../../../../util/router-compatibility';
import { Loader } from '@orijinworks/frontend-commons';
import useLearningPlanProgressHook from './useLearningPlanProgressHook';
import LearningPlanBreadcrumbs from '../components/learning-plan-breadcrumbs/learning-plan-breadcrumbs';
import styles from './learning-plan-progress.style';
import { Stack } from '@mui/material';
import { makeStyles } from '@material-ui/styles';
import { PROGRESS_STATUS } from '../constants/learning-plans-constants';

const useStyles = makeStyles(styles);
const LearningPlanProgress = () => {
  const { key } = useParams();

  const classes = useStyles();
  const {
    learningPlan,
    pillarProgress,
    loading,
    error,
    handleBackToList,
    handleNavigateToDetails,
    getStatusColor,
  } = useLearningPlanProgressHook(key);

  const breadcrumbItems = [
    {
      label: 'Learning Plans',
      onClick: handleBackToList,
    },
    {
      label: learningPlan?.key || key || 'Learning Plan',
    },
    {
      label: 'Aggregated Progress Report',
      isCurrent: true,
    },
  ];

  if (loading) {
    return (
      <Grid container component="div" className={classes.newGridContent}>
        <Loader isLoading={true} />
      </Grid>
    );
  }

  if (error || !learningPlan) {
    return (
      <Grid container component="div" className={classes.newGridContent}>
        <Box display="flex" flexDirection="column" gap={2} width="100%">
          <LearningPlanBreadcrumbs items={breadcrumbItems} />
          <Box className={classes.errorContainer}>
            <Typography variant="h6" color="error">
              {error || 'Learning plan not found'}
            </Typography>
          </Box>
        </Box>
      </Grid>
    );
  }

  return (
    <Suspense fallback={<Loader isLoading={true} />}>
      <Grid container component="div" className={classes.newGridContent}>
        <Grid container item>
          <LearningPlanBreadcrumbs items={breadcrumbItems} />
        </Grid>

        <Box className={classes.contentContainer}>
          <Box className={classes.headerSection}>
            <Paper elevation={2} className={classes.headerCard}>
              <Box className={classes.cardHeader}>
                <Box className={classes.titleWrapper}>
                  <Typography variant="h4" className={classes.title}>
                    {learningPlan.title} - Aggregated Report
                  </Typography>
                  <Typography variant="body1" className={classes.subtitle}>
                    Overview of learner progress across all jurisdictions
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          <Box className={classes.pillarsSection}>
            <Typography variant="h5" className={classes.pillarsLabel}>
              Pillar Completion Overview
            </Typography>
            <Paper elevation={2} className={classes.pillarsCard}>
              <Stack spacing={2} className={classes.pillarsStack}>
                {pillarProgress?.length > 0 ? (
                  pillarProgress.map((pillar) => {
                    return (
                      <Box key={pillar.id} className={classes.pillarItem}>
                        <Typography variant="h6" className={classes.pillarTitle}>
                          {pillar.title}
                        </Typography>
                        
                        <Box className={classes.progressBarContainer}>
                        <Box className={classes.progressBarWrapper}>
                          <Box className={classes.progressBar}>
                            {pillar.completedPercent > 0 && (
                              <Box
                                className={classes.progressSegment}
                                style={{
                                  width: `${pillar.completedPercent}%`,
                                  backgroundColor: getStatusColor(PROGRESS_STATUS.COMPLETED),
                                }}
                              >
                                <Typography variant="body2" className={classes.progressSegmentText}>
                                  {Math.round(pillar.completedPercent)}% Completed
                                </Typography>
                              </Box>
                            )}
                            {pillar.inProgressPercent > 0 && (
                              <Box
                                className={classes.progressSegment}
                                style={{
                                  width: `${pillar.inProgressPercent}%`,
                                  backgroundColor: getStatusColor(PROGRESS_STATUS.IN_PROGRESS),
                                }}
                              >
                                <Typography variant="body2" className={classes.progressSegmentText}>
                                  {Math.round(pillar.inProgressPercent)}%
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                          <Box className={classes.progressLegend}>
                          <Box className={classes.legendItem}>
                            <Box
                              className={classes.legendSquare}
                              style={{ backgroundColor: getStatusColor(PROGRESS_STATUS.COMPLETED) }}
                            />
                            <Typography variant="body2" className={classes.legendText}>
                              Completed ({pillar.completed})
                            </Typography>
                          </Box>
                          <Box className={classes.legendItem}>
                            <Box
                              className={classes.legendSquare}
                              style={{ backgroundColor: getStatusColor(PROGRESS_STATUS.IN_PROGRESS) }}
                            />
                            <Typography variant="body2" className={classes.legendText}>
                              In Progress ({pillar.inProgress})
                            </Typography>
                          </Box>
                          </Box>
                          <Box className={classes.progressSummary}>
                            <Typography variant="body2" className={classes.summaryText}>
                              {pillar.completed} completed | {pillar.inProgress} in progress
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" className={classes.noPillars}>
                    No progress data available for this learning plan.
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Box>

          <Box className={classes.footerSection}>
            <Typography variant="body2" className={classes.footerText}>
              Last Updated: {new Date().toLocaleString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZoneName: 'short'
              })} | Report ID: {learningPlan.reportId || 'N/A'} | Showing {learningPlan.learnerCount || 0} of {learningPlan.totalLearners || 0} learners
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Suspense>
  );
};

export default withRouter(LearningPlanProgress);

